import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import ImageUploader from "@/components/ImageUploader";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronUp, ChevronDown, Trash2, Plus, Save, Eye } from "lucide-react";
import { toast } from "sonner";

const SECTION_TYPES = [
  { value: "hero", label: "Hero (banner principale)" },
  { value: "product_grid", label: "Griglia prodotti" },
  { value: "best_sellers", label: "Più venduti" },
  { value: "recommended", label: "Prodotti consigliati" },
  { value: "text", label: "Blocco testo" },
  { value: "image_block", label: "Blocco immagine" },
  { value: "gallery", label: "Galleria immagini" },
  { value: "video", label: "Video embed" },
  { value: "newsletter", label: "Iscrizione newsletter" },
  { value: "cta", label: "Call to action" },
  { value: "faq", label: "FAQ" },
  { value: "contact", label: "Form contatti" },
  { value: "social_share", label: "Social share" },
];

const defaultContent = (type) => ({
  hero: { title: "Titolo principale", subtitle: "Sottotitolo", cta_text: "Scopri", cta_link: "#", background_image: "" },
  product_grid: { title: "I nostri prodotti", subtitle: "", filter_category: "", limit: 12 },
  best_sellers: { title: "I più venduti", limit: 4 },
  recommended: { title: "Consigliati per te", skus: [] },
  text: { title: "Titolo sezione", body: "Inserisci qui il testo…" },
  image_block: { image_url: "", caption: "", link: "" },
  gallery: { images: [] },
  video: { url: "", caption: "" },
  newsletter: { title: "Iscriviti", subtitle: "Resta aggiornato", button_text: "Iscriviti" },
  cta: { title: "Pronto a iniziare?", button_text: "Contattaci", link: "#" },
  faq: { items: [{ q: "", a: "" }] },
  contact: { title: "Contattaci", email: "" },
  social_share: { networks: ["facebook", "twitter", "linkedin", "whatsapp"] },
}[type] || {});

export default function CmsEditor() {
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tenantSlug, setTenantSlug] = useState("");

  useEffect(() => {
    api.get("/cms/my-page?slug=home").then((r) => setPage(r.data));
    api.get("/tenant/me").then((r) => setTenantSlug(r.data.slug));
  }, []);

  if (!page) return <AdminLayout><div className="p-10">Loading…</div></AdminLayout>;

  const updateSection = (idx, updates) => {
    const sections = [...page.sections];
    sections[idx] = { ...sections[idx], ...updates };
    setPage({ ...page, sections });
  };
  const updateContent = (idx, key, val) => {
    const sections = [...page.sections];
    sections[idx] = { ...sections[idx], content: { ...sections[idx].content, [key]: val } };
    setPage({ ...page, sections });
  };
  const moveSection = (idx, direction) => {
    const sections = [...page.sections];
    const j = idx + direction;
    if (j < 0 || j >= sections.length) return;
    [sections[idx], sections[j]] = [sections[j], sections[idx]];
    setPage({ ...page, sections });
  };
  const removeSection = (idx) => {
    setPage({ ...page, sections: page.sections.filter((_, i) => i !== idx) });
  };
  const addSection = (type) => {
    setPage({
      ...page,
      sections: [...page.sections, {
        id: `tmp-${Date.now()}`, type, position: page.sections.length,
        visible: true, content: defaultContent(type),
      }],
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cms/my-page", { slug: "home", title: page.title || "", sections: page.sections, is_homepage: true });
      toast.success("Pagina salvata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10 max-w-5xl" data-testid="cms-editor">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="overline text-brand mb-2">CMS · Editor sezioni</div>
            <h1 className="font-heading font-bold text-4xl tracking-tight">Modifica la tua vetrina</h1>
            <p className="text-muted-foreground mt-2 text-sm">Aggiungi, riordina (frecce ↑↓) e personalizza le sezioni della tua home.</p>
          </div>
          <div className="flex gap-2">
            {tenantSlug && (
              <a href={`/s/${tenantSlug}`} target="_blank" rel="noreferrer" data-testid="cms-view-site"
                className="inline-flex items-center gap-1.5 px-4 h-10 rounded-sm border border-foreground text-sm hover:bg-foreground hover:text-white transition-colors">
                <Eye className="w-4 h-4" /> Vedi il sito
              </a>
            )}
            <button data-testid="cms-save-btn" onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 px-5 h-10 rounded-sm bg-brand hover:bg-brand-hover text-white text-sm font-medium transition-colors disabled:opacity-50">
              <Save className="w-4 h-4" /> Salva
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {page.sections.map((sec, idx) => (
            <div key={sec.id} className="bg-white border border-border rounded-sm" data-testid={`cms-section-${idx}`}>
              <div className="flex items-center justify-between px-5 py-3 bg-[#FAFAFA] border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-0.5">
                    <button data-testid={`cms-section-up-${idx}`} onClick={() => moveSection(idx, -1)} disabled={idx === 0}
                      className="w-6 h-5 rounded-sm border border-border flex items-center justify-center hover:bg-accent disabled:opacity-30">
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button data-testid={`cms-section-down-${idx}`} onClick={() => moveSection(idx, 1)} disabled={idx === page.sections.length - 1}
                      className="w-6 h-5 rounded-sm border border-border flex items-center justify-center hover:bg-accent disabled:opacity-30">
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">#{idx + 1}</span>
                  <span className="font-medium text-sm">{SECTION_TYPES.find((t) => t.value === sec.type)?.label || sec.type}</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs">
                    <Switch data-testid={`cms-section-visible-${idx}`} checked={sec.visible !== false}
                      onCheckedChange={(v) => updateSection(idx, { visible: v })} />
                    Visibile
                  </label>
                  <button data-testid={`cms-section-remove-${idx}`} onClick={() => removeSection(idx)}
                    className="w-7 h-7 rounded-sm border border-border flex items-center justify-center text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-3">
                <SectionFields section={sec} idx={idx} updateContent={updateContent} />
              </div>
            </div>
          ))}

          <div className="bg-white border border-dashed border-border rounded-sm p-5">
            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-brand" />
              <span className="text-sm font-medium">Aggiungi nuova sezione</span>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 mt-3">
              {SECTION_TYPES.map((t) => (
                <button key={t.value} data-testid={`cms-add-${t.value}`} onClick={() => addSection(t.value)}
                  className="text-xs px-3 h-9 rounded-sm border border-border hover:bg-accent text-left transition-colors">
                  + {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function SectionFields({ section, idx, updateContent }) {
  const c = section.content || {};

  switch (section.type) {
    case "hero":
      return (
        <div className="space-y-3">
          <CmsInput k="title" label="Titolo" c={c} idx={idx} updateContent={updateContent} />
          <CmsInput k="subtitle" label="Sottotitolo" c={c} idx={idx} updateContent={updateContent} />
          <div className="grid grid-cols-2 gap-3">
            <CmsInput k="cta_text" label="Testo bottone" c={c} idx={idx} updateContent={updateContent} />
            <CmsInput k="cta_link" label="Link bottone" c={c} idx={idx} updateContent={updateContent} />
          </div>
          <ImageUploader value={c.background_image} onChange={(v) => updateContent(idx, "background_image", v)} label="Immagine di sfondo" testid={`cms-section-${idx}-bg`} />
        </div>
      );
    case "product_grid":
      return (
        <div className="space-y-3">
          <CmsInput k="title" label="Titolo" c={c} idx={idx} updateContent={updateContent} />
          <CmsInput k="subtitle" label="Sottotitolo" c={c} idx={idx} updateContent={updateContent} />
          <div className="grid grid-cols-2 gap-3">
            <CmsInput k="filter_category" label="Categoria filtro (vuoto = tutti)" c={c} idx={idx} updateContent={updateContent} />
            <CmsInput k="limit" type="number" label="Max prodotti" c={c} idx={idx} updateContent={updateContent} />
          </div>
        </div>
      );
    case "best_sellers":
      return (
        <div className="space-y-3">
          <CmsInput k="title" label="Titolo" c={c} idx={idx} updateContent={updateContent} />
          <CmsInput k="limit" type="number" label="Max prodotti" c={c} idx={idx} updateContent={updateContent} />
        </div>
      );
    case "text":
      return (
        <div className="space-y-3">
          <CmsInput k="title" label="Titolo" c={c} idx={idx} updateContent={updateContent} />
          <CmsTextarea k="body" label="Testo (supporta Markdown leggero)" rows={6} c={c} idx={idx} updateContent={updateContent} />
        </div>
      );
    case "image_block":
      return (
        <div className="space-y-3">
          <ImageUploader value={c.image_url} onChange={(v) => updateContent(idx, "image_url", v)} label="Immagine" testid={`cms-section-${idx}-img`} />
          <CmsInput k="caption" label="Caption" c={c} idx={idx} updateContent={updateContent} />
          <CmsInput k="link" label="Link (opzionale)" c={c} idx={idx} updateContent={updateContent} />
        </div>
      );
    case "gallery": {
      const imgs = c.images || [];
      return (
        <div className="space-y-3">
          {imgs.map((img, i) => (
            <div key={i} className="flex gap-2 items-center">
              <ImageUploader value={img} onChange={(v) => {
                const arr = [...imgs]; arr[i] = v; updateContent(idx, "images", arr);
              }} label="" testid={`cms-section-${idx}-gallery-${i}`} />
              <button type="button" onClick={() => updateContent(idx, "images", imgs.filter((_, ii) => ii !== i))}
                className="text-xs text-red-700">Rimuovi</button>
            </div>
          ))}
          <button type="button" data-testid={`cms-section-${idx}-gallery-add`}
            onClick={() => updateContent(idx, "images", [...imgs, ""])}
            className="px-3 h-9 rounded-sm border border-border text-xs hover:bg-accent">+ Aggiungi immagine</button>
        </div>
      );
    }
    case "video":
      return (
        <div className="space-y-3">
          <CmsInput k="url" label="URL video (YouTube/Vimeo embed o .mp4)" c={c} idx={idx} updateContent={updateContent} />
          <CmsInput k="caption" label="Caption" c={c} idx={idx} updateContent={updateContent} />
        </div>
      );
    case "newsletter":
      return (
        <div className="space-y-3">
          <CmsInput k="title" label="Titolo" c={c} idx={idx} updateContent={updateContent} />
          <CmsInput k="subtitle" label="Sottotitolo" c={c} idx={idx} updateContent={updateContent} />
          <CmsInput k="button_text" label="Testo bottone" c={c} idx={idx} updateContent={updateContent} />
        </div>
      );
    case "cta":
      return (
        <div className="space-y-3">
          <CmsInput k="title" label="Titolo" c={c} idx={idx} updateContent={updateContent} />
          <CmsInput k="button_text" label="Testo bottone" c={c} idx={idx} updateContent={updateContent} />
          <CmsInput k="link" label="Link" c={c} idx={idx} updateContent={updateContent} />
        </div>
      );
    case "faq": {
      const items = c.items || [];
      return (
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="space-y-1.5 border border-border rounded-sm p-3">
              <input className="w-full h-9 px-2 rounded-sm border border-border text-sm" placeholder="Domanda"
                value={it.q || ""} data-testid={`cms-section-${idx}-faq-q-${i}`}
                onChange={(e) => { const arr = [...items]; arr[i] = { ...arr[i], q: e.target.value }; updateContent(idx, "items", arr); }} />
              <textarea className="w-full px-2 py-1.5 rounded-sm border border-border text-sm" placeholder="Risposta"
                value={it.a || ""} rows={2} data-testid={`cms-section-${idx}-faq-a-${i}`}
                onChange={(e) => { const arr = [...items]; arr[i] = { ...arr[i], a: e.target.value }; updateContent(idx, "items", arr); }} />
              <button type="button" onClick={() => updateContent(idx, "items", items.filter((_, ii) => ii !== i))}
                className="text-xs text-red-700">Rimuovi</button>
            </div>
          ))}
          <button type="button" onClick={() => updateContent(idx, "items", [...items, { q: "", a: "" }])}
            className="px-3 h-9 rounded-sm border border-border text-xs hover:bg-accent">+ Aggiungi FAQ</button>
        </div>
      );
    }
    case "contact":
      return (
        <div className="space-y-3">
          <CmsInput k="title" label="Titolo" c={c} idx={idx} updateContent={updateContent} />
          <CmsInput k="email" label="Email destinatario" c={c} idx={idx} updateContent={updateContent} />
        </div>
      );
    case "social_share":
      return <p className="text-xs text-muted-foreground">Pulsanti di condivisione automatici su Facebook, X, LinkedIn, WhatsApp, Email.</p>;
    case "recommended":
      return <p className="text-xs text-muted-foreground">Mostra automaticamente prodotti correlati basati sulla categoria visualizzata.</p>;
    default:
      return null;
  }
}

// Stable components defined at module scope to PREVENT React from
// unmounting/remounting inputs on every keystroke (which was causing the
// "scrive una lettera alla volta" focus-loss bug).
const CmsInput = React.memo(function CmsInput({ k, label, type = "text", placeholder = "", c, idx, updateContent }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1 block capitalize">{label || k.replace(/_/g, " ")}</label>
      <input data-testid={`cms-section-${idx}-${k}`} type={type} value={c[k] ?? ""} placeholder={placeholder}
        onChange={(e) => updateContent(idx, k, type === "number" ? (parseFloat(e.target.value) || 0) : e.target.value)}
        className="w-full h-9 px-2 rounded-sm border border-border text-sm" />
    </div>
  );
});

const CmsTextarea = React.memo(function CmsTextarea({ k, label, rows = 4, c, idx, updateContent }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1 block">{label || k}</label>
      <textarea data-testid={`cms-section-${idx}-${k}`} value={c[k] ?? ""} rows={rows}
        onChange={(e) => updateContent(idx, k, e.target.value)}
        className="w-full px-2 py-1.5 rounded-sm border border-border text-sm" />
    </div>
  );
});
